// 11 §10.1 World screen as the overworld (2026-10-23 mockup "Overworld — click a node to walk
// there"): the World's places as nodes on a seeded valley (`map/overworld.ts`) drawn in the custom
// overworld art (docs/143), each place's pin above its art and its name below, joined by dotted roads;
// offers (quest notices) sit on their location's node and the selected one opens the quest card
// (bottom right) with Accept for a forming party's lead → `accept_offer`. Barless (16 §2):
// the screen draws its own top bar — the World plate (`ShellPlate`), the nav tabs and display name,
// the party chips (my roster: portrait, name in my player colour, where they stand, XP; a chip
// opens the hero sheet, whose bag opens the docs/71 inventory), gold and the menu. Bottom left, the dock: World/
// party chat, the Chronicle (last 50, newest first), who is here, and the Ledger lite (§10.7).
// Built from the component library (§10.10); layout classes `fk-world__*` live in world.css.
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { EmoteAnchor } from '../emote/EmoteBubble';
import { OFFLINE_TITLE, useLinkUp } from '../../core/stores/link';
import { useTable } from 'spacetimedb/react';
import { Almanac, Badge, Button, ButtonRow, Card, CardList, ClassIcon, Counter, Divider, EmptyState, Feed, HeroSprite, Meter, Modal, OryxIcon, Panel, PingMark, Pips as LibPips, Portrait, Quote, SkeletonList, useSettled } from '../components';
import { tables } from '../../bindings';
import { useSession } from '../../core/stores/session';
import { useRoute } from '../../core/stores/route';
import { useGenesis, useWorldOpening, useWorldStory, useChronicle, useLocationStatuses, useMyAccount, useMyHeroes, useMyLevelUpOffers, useNamedCharacters, useNames, useOffers, useOnline, useParties, useRegardRows, useWorld, useWorldHeroes, useWorldMembers, useWorldPartyMembers, useWorldSettings, useWorldClock, useQuestStages, useCampaignProgress, useBookChapters, usePartyWalks, useVillagePresence, useVillages, useUpgradeGifts, useHeroCallings, useHeroProgress } from '../../core/subscriptions/hooks';
import { heroWalk, isOpen } from '../../core/sim/progression';
import { rpc, useToasts } from '../../core/net/reducers';
import { identityHex, requireConn, tsToMs } from '../../core/net/connection';
import { mustering } from './BandCalls';
import { CLASS_NAMES, SPECIES_NAMES } from '../../core/sim/adapt';
import { actionById } from '../../core/sim/actions';
import { conditionWord, INST, OFFER, type Adventure } from '../../core/adventure/graph';
import { getBridge } from '../../core/encounter/session';
import { serverNow, smoothServerNow } from '../../core/time';
import { useOverlay } from '../overlay';
import { PlayOnline } from './PlayOnline';
import { GearStrip } from '../inventory/Inventory';
import { useIncomingTrades } from '../inventory/Trades';
import { openInventory } from '../inventory/store';
import { ChatDock, playerColour, usePlayerColours } from './ChatDock';
import { ColourButton } from './ColourPicker';
import { colourOpen } from '../../core/world/colours';
import { HeroForge } from './HeroForge';
import { OutfitPanel, useFounding } from './Outfitting';
import { MAX_HEROES } from './founding';
import { Shell, ShellMenuButton, ShellName, ShellNav, ShellPlate } from './Shell';
import { generateOverworld, pathLength, seedFrom, WALK_CELLS_PER_S, OW_H, OW_W, type MapSite, type Overworld as OverworldMap, type PlaceSlot } from './map/overworld';
import { useScreenCurtain } from '../../core/stores/screenCurtain';
import { useOverworldCamera } from './map/useOverworldCamera';
import { RoadDots } from './map/RoadDots';
import { SagaModal } from './intro/Saga';
import { display, OPENING, STORY } from '../../core/story/genesis';
import { CHAPTER, numberWord } from '../../core/story/chapter';
import { hourWordsIn, overworldKit, parseSeasonKey, seasonKey, skyWords, weekPhrase, type SeasonKey } from '../../core/story/calendar';
import { isNamed, worldTitle } from '../../core/story/worldName';
import { useSettings } from '../../core/stores/settings';
import { owHour, owLight, parseOwWeather, worldSky, type OwWeather } from './map/worldSky';
import { LandLight, LandSky, SeasonSky, WeatherSky } from './map/WorldSkyLayers';
import { CustomOverworldMap } from './map/CustomMapCanvas';
import { folderHasLights } from './map/customArt';
import { artBiome } from './map/overworldBiomes';
import { setPlaceSeed, townBand, usePlace } from '../../core/audio/places';
import { overworldAmbience, setAmbience } from '../../core/audio/ambience';
import { BAND_GAP, BAND_SHOWN, bandLine, restPoint, bandTrail, knotSpot, subPath, walkPlan, walkTurns, type BandWalk } from './map/bandWalk';
import { pingChart, useWorldPings, type WorldPing } from './map/worldPings';
import { parseWeather } from '../../game/weather/weather';

const HERO_STATUS = ['available', 'deployed', 'retired'];
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const SMALL_WORDS = new Set(['of', 'the', 'a', 'an', 'and', 'to', 'in', 'on', 'at', 'by']);
/** "the bog road" → "The Bog Road" (plates are small caps, so every word is capitalised). */
const titled = (s: string) => s.split(' ').map((w, i) => (i > 0 && SMALL_WORDS.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
/** 10 §7 gear slots by `item.equipped_slot`. */
export const SLOT_NAMES = ['Main hand', 'Off hand', 'Torso', 'Head', 'Cape', 'Trinket', 'Hands', 'Feet', 'Neck', 'Trinket', 'Legs', 'Waist'];
export const TIER_NAMES = ['', 'Plain', 'Fine', 'Masterwork'];
/** `sell_item` pays a quarter of `price(tier)`: 20 / 60 / 180 → 5 / 15 / 45 gold. */
export const SELL_PRICE = [0, 5, 15, 45];
/** fk-world `xp_to_reach(level) = 100·L·(L−1)/2`. */
// docs/119: the curve runs past 20 (laurels); the one copy lives beside the progression catalogue
export { MAX_LEVEL, xpToReach } from '../../core/sim/progression';
import { MAX_LEVEL, xpToReach } from '../../core/sim/progression';
/** Ledger axes (and anything else that used the old local component) use the library Pips. */
export const Pips = LibPips;

const clock = (ms: number | undefined) => new Date(ms ?? 0).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** Oryx glyph per offer archetype (05 §6: the eight side-quest modules). */
const ARCHETYPE_ICON: Record<string, string> = { combat: 'item_sword', defense: 'item_shield_round', escort: 'item_heart', retrieval: 'chest_gold_closed', negotiation: 'item_coin_bag', investigation: 'item_scroll', puzzle: 'item_key_gold', expedition: 'item_bow' };
/** `offer.blob` is a postcard `FrozenEnvelope { kind, version, ruleset_version, payload }` around the
 *  AdventureBlob (fk-world `refill_offers`): skip three varints, read the payload's length prefix. */
export function envelopePayload(b: Uint8Array): Uint8Array | undefined {
  let i = 0;
  const varint = (): number | undefined => { let v = 0, shift = 0; while (i < b.length) { const x = b[i++]!; v += (x & 0x7f) * 2 ** shift; if (!(x & 0x80)) return v; shift += 7; } return undefined; };
  for (let k = 0; k < 3; k++) if (varint() === undefined) return undefined;
  const n = varint();
  return n === undefined || i + n > b.length ? undefined : b.subarray(i, i + n);
}
/** Offer blobs decode once per offer (the blob is fixed when the offer is posted). */
const offerCache = new Map<string, Adventure | null>();
function offerAdventure(id: bigint, blob: Uint8Array): Adventure | undefined {
  const key = id.toString();
  if (!offerCache.has(key)) {
    let a: Adventure | null = null;
    try { const payload = envelopePayload(blob); if (payload) a = getBridge()?.decodeAdventure(payload) ?? null; } catch { a = null; }
    if (a || getBridge()) offerCache.set(key, a);
    return a ?? undefined;
  }
  return offerCache.get(key) ?? undefined;
}

/** The World's instances (where a party stands on the overworld). */
const useWorldInstances = (worldId: bigint | undefined) => useTable(tables.adventureInstance.where((r) => r.worldId.eq(worldId ?? -1n)), { enabled: worldId !== undefined })[0];

export type Offer = ReturnType<typeof useOffers>[number];
export type Hero = ReturnType<typeof useWorldHeroes>[number];
type Loc = ReturnType<typeof useLocationStatuses>[number];

type VillageRow = ReturnType<typeof useVillages>[number];
/** One overworld node: the World itself (home), a location by index or a village (docs/51), with its open notices. */
export interface Place { key: string; name: string; home?: boolean; loc?: Loc; village?: VillageRow; offers: Offer[] }
const locKey = (index: number) => `loc-${index}`;
/** docs/51: villages hold `location_index` 10 + their index; the haven's (10) is the World's seat. */
export const VILLAGE_BASE = 10;
const keyOfLocation = (index: number) => (index === VILLAGE_BASE ? 'home' : locKey(index));
/** `party_walk` place numbers (−1 is the World's seat) ↔ chart keys. */
const placeKeyOf = (n: number) => (n < 0 ? 'home' : locKey(n));
const placeNumOf = (key: string) => (key === 'home' ? -1 : Number(key.slice(4)));
const SLOTS: readonly PlaceSlot[] = ['wild', 'road', 'crossing'];
/** The forge slot of a location key (`location_index` 0 wild, 1 road, 2 crossing). */
const slotOfKey = (key: string): Exclude<PlaceSlot, 'home'> | undefined => SLOTS[placeNumOf(key)] as Exclude<PlaceSlot, 'home'> | undefined;
const norm = (s: string) => s.trim().toLowerCase();
const dangerOf = (a: Adventure | undefined) => (a ? Math.max(1, Math.min(3, Math.round(a.difficultyBp / 3333))) : undefined);
const pitchOf = (o: { pitch: string; asker: string }) => o.pitch.replace('{asker}', o.asker);

/** The forming party I lead (the one on the route first) and what `accept_offer` would say. */
export function useAcceptance(worldId: bigint) {
  const me = useSession((s) => s.identity ?? '');
  const routeParty = useRoute((s) => s.partyId);
  const parties = useParties(worldId);
  const members = useWorldPartyMembers(worldId);
  const mine = parties.filter((p) => identityHex(p.lead) === me && p.state === 0).sort((a, b) => (a.id === routeParty ? -1 : b.id === routeParty ? 1 : a.id < b.id ? -1 : 1));
  const party = mine[0];
  const deployed = party ? members.filter((m) => m.partyId === party.id && m.deployed) : [];
  const humans = new Set(deployed.map((m) => identityHex(m.owner))).size;
  const anyLedByOther = parties.some((p) => p.state === 0 && identityHex(p.lead) !== me && members.some((m) => m.partyId === p.id && identityHex(m.owner) === me));
  const reason = (o: { minHumans: number; maxHumans: number }): string | undefined => {
    if (!party) return anyLedByOther ? 'only your band\'s lead sets out' : 'no band has mustered yet — form a party in the lobby';
    if (deployed.length === 0) return 'deploy at least one hero';
    if (humans < o.minHumans || humans > o.maxHumans) return `this offer wants ${o.minHumans}–${o.maxHumans} humans (you have ${humans})`;
    return undefined;
  };
  return { party, deployed, humans, reason };
}

/** Node icon per place: the notice's archetype, the keep for home, a tick for a place with history. */
function placeIcon(p: Place, lead: Offer | undefined): ReactNode {
  if (lead) return <OryxIcon name={ARCHETYPE_ICON[lead.archetype.toLowerCase()] ?? 'item_scroll'} scale={2} title={lead.archetype} />;
  if (p.home) return <OryxIcon name="gate_closed" scale={2} title="the World's seat" />;
  if (p.village) return <OryxIcon name="door_closed" scale={2} title="a village" />;
  if (p.loc && p.loc.adventures > 0) return <span className="fk-world__node-tick" aria-hidden="true">✓</span>;
  return <OryxIcon name="item_scroll" scale={2} title="waypoint" />;
}

/** docs/36: a band on the chart — its heroes, and where it stands or which way it is walking. */
export interface BandView { id: string; partyId?: bigint; name: string; heroes: Hero[]; at: string; walk?: BandWalk; mine: boolean; lead: boolean; out: boolean; leadOwner?: string }
/** The place a walking band last passed or stands at (where a new walk sets out from). */
export function bandPlace(ow: OverworldMap, b: BandView, now: number): string {
  if (!b.walk) return b.at;
  const turns = walkTurns(ow, b.walk, now);
  if (!turns) return b.walk.to;
  return pathLength(turns.from) < pathLength(turns.to) ? b.walk.from : b.walk.to;
}

/** What the chart is lit and dressed for: the World's season, country, weather and hour (docs/23, docs/24). */
export interface ChartSky { season?: SeasonKey; biome?: string; weather?: OwWeather; wxRaw?: string; hour: number }
/** The World's sky over the chart; `?sky=winter,snow,temperate,18.3` previews any season, weather, country and hour (review only). */
export function useChartSky(worldId: bigint | undefined): ChartSky {
  const clock = useWorldClock(worldId);
  const pin = useMemo(() => new URLSearchParams(window.location.search).get('sky')?.split(',') ?? [], []);
  const pinHour = pin[3] ? Number(pin[3]) : NaN;
  return {
    season: parseSeasonKey(pin[0]) ?? seasonKey(clock?.season),
    biome: pin[2] || clock?.biome,
    weather: parseOwWeather(pin[1]) ?? parseOwWeather(clock?.weather),
    wxRaw: pin[1] ?? clock?.weather,
    hour: Number.isFinite(pinHour) ? pinHour : owHour((worldId ?? 0n).toString(), clock?.weekIndex),
  };
}

/** The overworld: terrain, roads, place nodes with their notices, the bands walking the roads. */
function Overworld({ worldId, ow, places, selected, onSelect, bands, colours, featured, sky }: {
  worldId: bigint; ow: OverworldMap; places: Place[]; selected: string | undefined; onSelect: (key: string) => void;
  bands: BandView[]; colours: Map<string, number>; featured: Offer | undefined; sky: ChartSky;
}) {
  const world = useWorld(worldId);
  const members = useWorldMembers(worldId).length;
  const me = useSession((s) => s.identity ?? '');
  // docs/78 BW5: right-click pings the chart for everyone in the World (Shift: a warning)
  const pings = useWorldPings(worldId);
  const names = useNames(worldId);
  return <OverworldChart soundKey={worldId.toString()} title={worldTitle(world)} ow={ow} places={places} selected={selected} onSelect={onSelect} bands={bands} colours={colours} featured={featured}
    sky={sky} members={members} me={me} pings={pings} names={names} onPing={(x, y, warn) => pingChart(worldId, x, y, warn ? 2 : 0)} />;
}

/** A place's pin above its art (docs/143 OW1): the notice's archetype, the seat, a village, a tick or a waypoint. */
function PlacePin({ p, lead, locked }: { p: Place; lead: Offer | undefined; locked: boolean }) {
  return (
    <span className="fk-world__node-pin">
      <span className="fk-world__node-icon">
        {locked ? <span className="fk-world__node-lock" aria-hidden="true" /> : placeIcon(p, lead)}
        {p.offers.length > 1 && <span className="fk-world__node-count">{p.offers.length}</span>}
      </span>
    </span>
  );
}

/**
 * The World chart itself, from data alone (the World screen's hooks feed it; `?chart=1` drives it with
 * none, docs/143): the custom art for the realm's country, season and time of day under the hour's light
 * and the week's weather, the dotted routes, each place's art with its pin above and its name below
 * (both select it, as the art does), the bands and the pings.
 */
export function OverworldChart({ soundKey, title, ow, places, selected, onSelect, bands, colours, featured, sky: chartSky, members, me, pings, names, onPing }: {
  soundKey: string; title: string; ow: OverworldMap; places: Place[]; selected: string | undefined; onSelect: (key: string) => void;
  bands: BandView[]; colours: Map<string, number>; featured: Offer | undefined; sky: ChartSky;
  members: number; me: string; pings: WorldPing[]; names: Record<string, string>; onPing?: (x: number, y: number, warn: boolean) => void;
}) {
  const seed = ow.seed;
  // docs/23 Y8: the valley wears the season — the realm's country as it looks now
  const { season, biome, weather, wxRaw, hour } = chartSky;
  const kit = overworldKit(biome, season);
  const sky = useMemo(() => worldSky(season, biome, weather), [season, biome, weather]);
  // docs/19 at chart scale: the week's hour lights the valley — the sun's side, the shade, long shadows, lamps at dusk
  const lightingPref = useSettings((s) => s.lighting);
  const light = useMemo(() => (lightingPref === 'off' ? undefined : owLight(hour, kit, weather)), [lightingPref, hour, kit, weather]);
  const soft = lightingPref === 'soft' ? 0.6 : 1;
  const cast = useMemo(() => (light ? { ...light.cast, alpha: light.cast.alpha * soft } : undefined), [light, soft]);
  // docs/143 OW3: the art of the realm's country and season, its night art after dark
  const art = artBiome(biome), artSeason = season ?? 'summer', time = light?.phase === 'night' ? 'night' : 'day';
  const windowsLit = time === 'night' && folderHasLights(art, artSeason);
  // docs/36: the place's sound under the chart — the country, the river, the week's weather
  useEffect(() => {
    setAmbience(overworldAmbience(soundKey, biome, season, parseWeather(wxRaw), sky.ice > 0.5));
  }, [soundKey, biome, season, wxRaw, sky.ice]);
  useEffect(() => () => setAmbience(undefined), []);
  // docs/28 L6: rain and snow ring the river only where a front is passing over it right now
  const stage = useRef<HTMLDivElement>(null);
  // docs/42 on the chart: wheel / pinch / Q–E zoom, WASD / drag / edge pans, V home (`map/useOverworldCamera`)
  const mapEl = useRef<HTMLDivElement>(null);
  useOverworldCamera(mapEl, stage);
  const pingRef = useRef(onPing);
  pingRef.current = onPing;
  // docs/141 SF2: going into a place from the chart, the chart zooms into my band as the screen fades
  useEffect(() => {
    const zoom = () => {
      const map = mapEl.current;
      if (!map) return;
      const mr = map.getBoundingClientRect();
      const at = map.querySelector<HTMLElement>('.fk-world__band--mine .fk-world__token--lead') ?? map.querySelector<HTMLElement>('.fk-world__band--mine');
      const r = at?.getBoundingClientRect();
      map.style.transformOrigin = r ? `${r.left + r.width / 2 - mr.left}px ${r.top + r.height / 2 - mr.top}px` : '50% 50%';
      map.dataset.zooming = 'true';
    };
    useScreenCurtain.setState({ zoom });
    // a move called off under the black (the chart is still here as it lifts): back to the camera's own view
    const off = useScreenCurtain.subscribe((c) => { if (c.phase === 'opening' || c.phase === 'idle') delete mapEl.current?.dataset.zooming; });
    return () => { off(); if (useScreenCurtain.getState().zoom === zoom) useScreenCurtain.setState({ zoom: undefined }); };
  }, []);
  useEffect(() => {
    const map = mapEl.current;
    if (!map) return;
    const onMenu = (e: MouseEvent) => {
      const st = stage.current;
      if (!st || (e.target as Element | null)?.closest?.('.fk-world__bandbox, .fk-world__dock, .fk-world__bar, .fk-modal')) return;
      e.preventDefault();
      const r = st.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * OW_W, y = ((e.clientY - r.top) / r.height) * OW_H;
      if (x < 0 || y < 0 || x > OW_W || y > OW_H) return;
      pingRef.current?.(x, y, e.shiftKey);
    };
    map.addEventListener('contextmenu', onMenu);
    return () => map.removeEventListener('contextmenu', onMenu);
  }, []);
  const water = useMemo(() => ({
    precip: sky.precip, amount: Math.min(1, sky.precipCount / 140),
    under: !sky.fronts ? undefined : (): Array<[number, number]> => {
      const st = stage.current;
      if (!st) return [];
      const r = st.getBoundingClientRect();
      // a front fades out over its outer quarters (its mask): count the middle of it
      return [...(st.closest('.fk-world__map')?.querySelectorAll('.fk-ow-front') ?? [])].map((f) => {
        const b = f.getBoundingClientRect(), pad = b.width * 0.2;
        return [(b.left + pad - r.left) / r.width, (b.right - pad - r.left) / r.width] as [number, number];
      });
    },
  }), [sky]);
  // bands resting at one place stand beside its art, the first to the right, the next to the left, then further out
  const restSlot = (b: BandView) => {
    const at = b.walk ? b.walk.to : b.at;
    return bands.filter((x) => (x.walk ? x.walk.to : x.at) === at).indexOf(b);
  };
  return (
    <div ref={mapEl} className={`fk-world__map ${season ? `fk-world__map--${season}` : ''} ${biome ? `fk-world__map--${biome}` : ''}`} aria-label={`Overworld of ${title}`}>
      <OwVoid />
      <div ref={stage} className="fk-world__stage" style={{ ['--ow-w' as string]: OW_W, ['--ow-h' as string]: OW_H, ['--ow-grade' as string]: sky.grade || 'none' } as CSSProperties} data-testid="overworld" data-seed={seed} data-kit={kit} data-biome={art} data-time={time} data-weather={weather ?? ''} data-season={season ?? ''} data-hour={light ? light.hour : ''} data-phase={light?.phase ?? ''}>
        <div className="fk-ow-plinth" aria-hidden="true" />
        <div className="fk-ow-plinth__foot" aria-hidden="true" />
        <CustomOverworldMap ow={ow} biome={art} season={artSeason} time={time} cast={cast} scale={2} animate={!sky.ice} ice={sky.ice} weather={water} />
        {light && <LandLight light={light} soft={soft} />}
        {/* docs/132 OP17: night art that lights its own windows draws them above the light, in place of the lamp glows */}
        {windowsLit && <CustomOverworldMap ow={ow} biome={art} season={artSeason} time="night" lights scale={2} />}
        {light && light.lamps > 0 && !windowsLit && places.map((p) => {
          const n = ow.nodes[p.key], a = ow.arts[p.key];
          if (!n || !(p.home || p.village || a === 'town' || a === 'village' || a === 'castle' || a === 'tower' || a === 'shrine' || a === 'abbey' || a === 'inn' || a === 'port')) return null;
          return <i key={`lamp-${p.key}`} className="fk-ow-lamp" aria-hidden="true" style={{ ['--x' as string]: n.x, ['--y' as string]: n.y, opacity: light.lamps * soft } as CSSProperties} />;
        })}
        <LandSky sky={sky} />
        <div className="fk-ow-rim" aria-hidden="true" />
        <div className="fk-ow-rim__edge" aria-hidden="true" />
        <svg className="fk-roads fk-roads--chart" viewBox={`0 0 ${OW_W * 24} ${OW_H * 24}`} aria-hidden="true">
          {ow.roads.filter((r) => !r.a.startsWith('edge:')).map((r) => <RoadDots key={`${r.a}-${r.b}`} path={r.path} skip={12} step={8} size={2} className={selected && (r.a === selected || r.b === selected) ? 'fk-roads__road--lit' : ''} />)}
        </svg>
        {places.map((p) => {
          const n = ow.nodes[p.key];
          if (!n) return null;
          const main = p.offers.find((o) => !o.side);
          const lead = (featured && p.offers.includes(featured) ? featured : undefined) ?? main ?? p.offers[0];
          const locked = p.offers.length > 0 && p.offers.every((o) => o.minHumans > members);
          const kind = locked ? 'locked' : main ? 'main' : p.offers.length ? 'side' : p.village ? 'village' : 'waypoint';
          const alarm = !!p.village?.alarm;
          const sel = selected === p.key;
          const cond = p.loc ? conditionWord(p.loc.condition) : undefined;
          const title = `${titled(p.name)}${p.home ? ' — the World\'s seat' : ''}${p.village ? ` — ${p.village.epithet}` : ''}${alarm ? ' — trouble!' : ''}${cond ? ` — ${cond}, ${p.loc!.adventures} adventure${p.loc!.adventures === 1 ? '' : 's'}` : ''}${p.offers.length ? ` — ${p.offers.length} notice${p.offers.length === 1 ? '' : 's'}` : ''}`;
          return (
            <div key={p.key} className={`fk-world__node fk-world__node--${kind} ${sel ? 'fk-world__node--sel' : ''} ${lead && featured === lead ? 'fk-world__node--featured' : ''} ${alarm ? 'fk-world__node--alarm' : ''}`} style={{ ['--x' as string]: n.x, ['--y' as string]: n.y } as CSSProperties} data-node={p.key} data-village={p.village?.id.toString()} data-art={ow.arts[p.key] ?? 'crossing'}>
              {/* docs/143 OW1: the button is the place's own art cell; its pin stands above and its name below, so the art shows */}
              <button type="button" className="fk-world__node-hit fk-hit" data-sfx="ui.leaves" onClick={() => onSelect(p.key)} aria-pressed={sel} title={title} aria-label={titled(p.name)}
                {...(p.loc ? { 'data-testid': 'location-card', 'data-condition': p.loc.condition } : p.home ? { 'data-testid': 'home-node' } : { 'data-testid': 'village-node' })}>
                <span className="fk-world__node-art" aria-hidden="true" />
                <PlacePin p={p} lead={lead} locked={locked} />
                <span className="fk-world__node-label">
                  <span className="fk-world__node-plate">{titled(p.name)}</span>
                  {p.loc && p.loc.condition !== 0 && <span className={`fk-world__node-cond fk-world__node-cond--${p.loc.condition < 0 ? 'bad' : 'good'}`}>{cond}</span>}
                </span>
                {alarm && <span className="fk-world__node-alarm" aria-hidden="true">!</span>}
              </button>
            </div>
          );
        })}
        {bands.map((b) => <BandMarker key={b.id} ow={ow} band={b} colours={colours} me={me} slot={restSlot(b)} />)}
        {pings.map((p) => <ChartPing key={p.id} p={p} colour={playerColour(colours.get(p.owner))} who={p.owner === me ? 'You' : names[p.owner] ?? 'Someone'} />)}
      </div>
      {sky.dressing && <SeasonSky kind={sky.dressing} />}
      <WeatherSky sky={sky} />
    </div>
  );
}

const range = (n: number) => Array.from({ length: n }, (_, i) => i);

/** docs/78 BW5: one ping on the chart (the board's look, docs/83: `PingMark`), placed by chart cell. */
function ChartPing({ p, colour, who }: { p: WorldPing; colour: string; who: string }) {
  return <PingMark kind={p.kind} colour={colour} who={who} owner={p.owner} className="fk-ow-ping" style={{ ['--x' as string]: p.x, ['--y' as string]: p.y } as CSSProperties} testId="map-ping" />;
}

/**
 * One band's heroes on the chart (docs/36, docs/78). At rest they stand in a knot over their
 * place's node, beside any other band there. Walking, they go single file along the road route
 * from the `party_walk` row: the leader (crowned) at the walk's pace, each follower the same road
 * `BAND_GAP` behind, easing out of the knot as the line reaches it and into the knot at the end.
 * Positions come from the walk row and the (smoothed) server clock alone, so every client puts
 * every hero at the same spot; they are written straight to the elements each frame.
 */
function BandMarker({ ow, band, colours, me, slot }: { ow: OverworldMap; band: BandView; colours: Map<string, number>; me: string; slot: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const tokens = useRef<Array<HTMLSpanElement | null>>([]);
  // docs/78 BW3: the lead player's heroes walk first, the leader's own first hero at the head
  const order = [...band.heroes].sort((a, b) => {
    const la = identityHex(a.owner) === band.leadOwner ? 0 : 1, lb = identityHex(b.owner) === band.leadOwner ? 0 : 1;
    return la - lb || (a.id < b.id ? -1 : 1);
  });
  const shown = order.slice(0, BAND_SHOWN);
  const n = shown.length;
  const crowned = !!band.leadOwner && !!shown[0] && identityHex(shown[0].owner) === band.leadOwner;
  const trail = useMemo(() => (band.walk ? bandTrail(ow, band.walk) : undefined), [ow, band.walk]);
  const departed = band.walk?.departed ?? 0;
  const fromAt = band.walk?.from;
  const restAt = band.walk ? band.walk.to : band.at;
  useEffect(() => {
    const wrap = ref.current;
    if (!wrap) return;
    const to = restPoint(ow, restAt), from = fromAt ? restPoint(ow, fromAt) : undefined;
    const put = (i: number, x: number, y: number, dx: number, moving: boolean) => {
      const el = tokens.current[i];
      if (!el) return;
      el.style.setProperty('--x', x.toFixed(3));
      el.style.setProperty('--y', y.toFixed(3));
      el.style.zIndex = String(Math.round(y * 10));     // lower on the chart, drawn in front
      if (dx) el.dataset.face = dx < 0 ? 'left' : 'right';
      el.dataset.walking = moving ? 'true' : 'false';
    };
    const rest = () => {
      if (to) for (let i = 0; i < n; i++) { const k = knotSpot(to, i, slot); put(i, k.x, k.y, 0, false); }
      wrap.dataset.walking = 'false';
    };
    const len = trail ? pathLength(trail.route) : 0;
    if (!trail || !to || len < 0.01) { rest(); return; }
    let raf = 0;
    const tick = () => {
      const walked = trail.tailLen + Math.max(0, ((smoothServerNow() - departed) / 1000) * WALK_CELLS_PER_S);
      if (walked - (n - 1) * BAND_GAP >= len) { rest(); return; }    // the last of the line is home
      const line = bandLine(trail, to, from, n, walked, slot);
      line.forEach((h, i) => put(i, h.x, h.y, h.dx, h.moving));
      wrap.dataset.walking = line.some((h) => h.moving) ? 'true' : 'false';
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [ow, trail, departed, fromAt, restAt, slot, n]);
  return (
    <div ref={ref} className={`fk-world__band ${band.mine ? 'fk-world__band--mine' : ''}`} data-testid="map-band" data-band={band.id} data-place={restAt} aria-label={`${band.name}: ${order.map((h) => h.name).join(', ')}`}>
      {shown.map((h, i) => {
        const owner = identityHex(h.owner);
        const lead = i === 0 && crowned;
        const more = i === n - 1 ? order.slice(BAND_SHOWN) : [];
        return (
          <span key={h.id.toString()} ref={(el) => { tokens.current[i] = el; }} className={`fk-world__token ${lead ? 'fk-world__token--lead' : band.leadOwner ? 'fk-world__token--follow' : ''}`}
            style={{ ['--fk-seat' as string]: playerColour(colours.get(owner)) } as CSSProperties} data-testid="map-token" data-hero-name={h.name} data-hero-id={h.id.toString()} data-lead={lead ? 'true' : undefined}>
            {lead && <span className="fk-world__token-crown" title={`${h.name} leads the band`} data-testid="map-token-crown"><OryxIcon name="item_149" scale={1} /></span>}
            <span className="fk-world__token-tag" title={owner === me ? `${h.name} (yours)` : h.name}>{h.name}</span>
            <span className="fk-world__token-body"><HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={CLASS_NAMES[h.class] ?? 'fighter'} scale={2} title={h.name} /></span>
            {more.length > 0 && <span className="fk-world__token-more">+{more.length}<EmoteAnchor heroes={more.map((x) => ({ id: x.id, name: x.name, mine: identityHex(x.owner) === me }))} /></span>}
            <EmoteAnchor heroes={[{ id: h.id, name: h.name, mine: owner === me }]} />
          </span>
        );
      })}
    </div>
  );
}

/**
 * Past the chart's edge (docs/38 BS7 on the overworld): dark fog of war, wisps drifting slowly
 * through it. It sits under the stage and does not pan with it, so the valley reads as a raised
 * piece of country with the fog far below.
 */
function OwVoid() {
  return (
    <div className="fk-ow-void" aria-hidden="true">
      {range(3).map((i) => <i key={i} className="fk-ow-void__wisp" style={{ top: `${-20 + i * 30}%`, animationDuration: `${140 + i * 50}s`, animationDelay: `${-i * 60}s` }} />)}
    </div>
  );
}

/** Notices on the board (each selects its node) and the quest card for the one in hand. */
function QuestBoard({ worldId, offers, featured, onPick, placeName, setOut, almanac }: { worldId: bigint; offers: Offer[]; featured: Offer | undefined; onPick: (o: Offer) => void; placeName: (o: Offer) => string; setOut?: (o: Offer) => number; almanac?: ReactNode }) {
  const up = useLinkUp();
  const go = useRoute((s) => s.go);
  const settled = useSettled(1000, worldId);
  const push = useToasts((s) => s.push);
  const { party, deployed, humans, reason } = useAcceptance(worldId);
  const realmBiome = useWorldClock(worldId)?.biome;
  const me = useSession((s) => s.identity ?? '');
  const [busy, setBusy] = useState<string | undefined>();
  const accept = async (o: Offer) => {
    if (!party) return;
    setBusy(`accept-${o.id}`);
    // docs/36: the band walks to the place first, then takes the work up there
    onPick(o);
    const wait = setOut?.(o) ?? 0;
    if (wait > 0) await new Promise((r) => setTimeout(r, Math.min(wait, 6000) + 150));
    // docs/120 VM6: band-mates in a village are asked whether to come; the rest walk out with the band
    const r = await rpc.takeUpOffer(o.id);
    setBusy(undefined);
    if (!r.ok) return;
    if (mustering([...requireConn().db.bandCall.iter()], me, o.id)) push(`Your band-mates in town are asked to come on “${o.title}”.`, 'info');
    else { push(`${party.name} takes up “${o.title}”.`, 'success'); go('localmap', { partyId: party.id }); }
  };
  const refresh = async () => { setBusy('refresh'); await rpc.refreshOffers(worldId); setBusy(undefined); };
  const f = featured;
  const a = f ? offerAdventure(f.id, f.blob) : undefined;
  const win = a?.outcomes.find((c) => c.row === 'Success');
  const danger = dangerOf(a);
  const why = f ? reason(f) : undefined;
  const fi = f ? offers.indexOf(f) : -1;
  // docs/23 Y7: a main quest carries why it cannot wait and the sky its fights are fought under
  const stages = useQuestStages(worldId);
  const qs = f ? stages.find((q) => q.offerId === f.id) : undefined;
  return (
    <div className="fk-world__right">
      <Panel variant="solid" className="fk-world__notices" label="Quest board" data-testid="offer-board">
        <header className="fk-world__section-head">
          <span className="fk-world__section-title">Quest board</span>
          <Badge tone="gold">{offers.length} posted</Badge>
          <Button small variant="ghost" className="fk-world__refresh" disabled={!!busy && busy !== 'refresh'} loading={busy === 'refresh'} onClick={() => void refresh()} data-testid="refresh-offers" title="Expire stale offers and fill the board back to four">Refresh</Button>
        </header>
        <p className={`fk-world__ready ${party ? 'fk-world__ready--on' : ''}`} data-testid="offer-party">
          <span className={`fk-world__dot ${party ? 'fk-world__dot--on' : ''}`} aria-hidden="true" />
          {party ? <span>Accepting as <strong>{party.name}</strong> ({deployed.length} deployed, {humans} human{humans === 1 ? '' : 's'})</span> : <span>Your band takes up work from here.</span>}
        </p>
        {offers.length === 0 && (settled
          ? <EmptyState className="fk-world__empty" glyph="⚑" title="The board is empty">Askers post new work every few hours; a refresh fills the board back to four.</EmptyState>
          : <SkeletonList n={3} kind="text" label="Loading offers" />)}
        <ul className="fk-world__notice-list">
          {offers.map((o) => {
            const w = reason(o);
            const sel = o === f;
            return (
              <li key={o.id.toString()} className={`fk-world__notice ${o.side ? '' : 'fk-world__notice--main'} ${sel ? 'fk-world__notice--sel' : ''}`} data-testid="offer-item" data-offer-id={o.id.toString()} data-archetype={o.archetype} onClick={() => onPick(o)}>
                <OryxIcon name={ARCHETYPE_ICON[o.archetype.toLowerCase()] ?? 'item_scroll'} scale={2} framed title={o.archetype} />
                <span className="fk-world__notice-body">
                  <button type="button" className="fk-card__title fk-world__notice-title" aria-pressed={sel} onClick={(e) => { e.stopPropagation(); onPick(o); }}>{o.title}</button>
                  <span className="fk-world__notice-sub">{o.side ? 'side' : 'main'} · {titled(placeName(o))}</span>
                </span>
                <Button small primary={!w} disabled={!up || !!busy || !!w} loading={busy === `accept-${o.id}`} title={!up ? OFFLINE_TITLE : w ?? `Accept for ${party?.name}`} onClick={(e) => { e.stopPropagation(); void accept(o); }} data-testid="offer-accept">Accept</Button>
                {w && <span className="fk-visually-hidden" data-testid="offer-reason">{w}</span>}
              </li>
            );
          })}
        </ul>
      </Panel>
      {almanac}
      {f && (
        <Panel variant="solid" key={f.id.toString()} className={`fk-world__quest ${f.side ? '' : 'fk-world__quest--main'}`} label="Quest" data-testid="quest-card" data-offer-id={f.id.toString()}>
          <div className="fk-world__quest-top">
            <span className={`fk-world__quest-tag ${f.side ? '' : 'fk-world__quest-tag--main'}`}>{f.side ? 'Side quest' : 'Main quest'}</span>
            <span className="fk-world__quest-kind">{f.archetype}</span>
            {danger !== undefined && <span className="fk-world__danger" aria-label={`danger ${danger} of 3`}>Danger{[0, 1, 2].map((i) => <i key={i} className={i < danger ? 'on' : ''} />)}</span>}
          </div>
          <h3 className="fk-world__quest-title">{f.title}</h3>
          <p className="fk-world__quest-pitch">{pitchOf(f)}</p>
          <p className="fk-world__quest-asker">— {f.asker}, at {titled(placeName(f))}</p>
          {qs && (
            <p className="fk-world__quest-now" data-testid="quest-why-now">
              {qs.timeJump && <span className="fk-world__quest-jump" data-testid="quest-time-jump">{qs.timeJump}</span>}
              {qs.whyNow && <span className="fk-world__quest-why">{qs.whyNow}</span>}
              <span className="fk-world__quest-sky" data-testid="quest-sky">Its fights: {hourWordsIn(qs.hour, realmBiome)}, {skyWords(qs.weather, realmBiome)}</span>
            </p>
          )}
          <div className="fk-world__quest-rewards">
            {win ? <>
              <span className="fk-world__reward fk-world__reward--xp">+{win.xp}<small>xp</small></span>
              {win.gold > 0 && <span className="fk-world__reward">{win.gold}<small>gold</small></span>}
              {win.lootDraws > 0 && <span className="fk-world__reward">{win.lootDraws}<small>loot draw{win.lootDraws === 1 ? '' : 's'}</small></span>}
            </> : <span className="fk-world__reward"><small>{f.minHumans}–{f.maxHumans} humans</small></span>}
            <span className="fk-world__quest-par">par {f.parMinutes} min</span>
          </div>
          <div className="fk-world__quest-foot">
            <span className="fk-world__quest-ready">
              <span className="fk-world__pips" aria-hidden="true">{Array.from({ length: Math.max(1, Math.min(4, Math.max(f.minHumans, humans))) }, (_, i) => <i key={i} className={i < humans ? 'on' : ''} />)}</span>
              <span>{why ? `${cap(why)}.` : `${humans} human${humans === 1 ? '' : 's'}, ${deployed.length} hero${deployed.length === 1 ? '' : 'es'} — ready to set out.`}</span>
            </span>
            <ButtonRow className="fk-world__quest-actions">
              {offers.length > 1 && <Button small variant="ghost" onClick={() => onPick(offers[(fi + 1) % offers.length]!)} title="Read the next notice" data-testid="offer-next">Next ›</Button>}
              <Button primary disabled={!up || !!busy || !!why} loading={busy === `accept-${f.id}`} title={!up ? OFFLINE_TITLE : why ?? `Accept for ${party?.name}`} onClick={() => void accept(f)} data-testid="offer-accept-featured">Accept</Button>
            </ButtonRow>
          </div>
        </Panel>
      )}
    </div>
  );
}

const SIZE_WORDS = ['hamlet', 'village', 'town'];

/**
 * docs/51: the village in hand on the chart — its name and what it is like, its buildings, a cry
 * for help if trouble is afoot, and the way in. Enter walks the band there first (as Accept does).
 */
function VillageCard({ v, here, onEnter, busy, follow }: { v: VillageRow; here: boolean; onEnter: () => void; busy: boolean; follow?: string }) {
  const buildings = [v.inn, v.shop, v.smithy, v.temple].filter(Boolean);
  return (
    <Panel variant="solid" className="fk-world__village fk-hit" label={v.name} data-testid="village-card" data-village={v.id.toString()}>
      <div className="fk-world__village-head">
        <OryxIcon name="door_closed" scale={2} framed />
        <div className="fk-world__village-id">
          <span className="fk-world__village-name">{v.name}</span>
          <span className="fk-world__village-sub">{v.index === 0 ? 'the haven' : `a ${SIZE_WORDS[v.size] ?? 'village'}`} · {v.epithet}</span>
        </div>
        <Button primary small loading={busy} disabled={busy || !!follow} onClick={onEnter} data-testid="village-enter" title={follow ? `${follow} leads your band: ping ${v.name} to ask them to walk there` : here ? `Walk into ${v.name}` : `Walk the band to ${v.name} and go in`}>{here ? 'Enter' : 'Go and enter'}</Button>
      </div>
      {v.alarm && <p className="fk-world__village-alarm" data-testid="village-card-alarm">“{v.alarm}”</p>}
      <p className="fk-world__village-text">{v.text}</p>
      <p className="fk-world__village-buildings">{buildings.join(' · ')}</p>
    </Panel>
  );
}

export function LedgerLite({ worldId }: { worldId: bigint }) {
  const named = [...useNamedCharacters(worldId)].sort((a, b) => (a.npc < b.npc ? -1 : 1));
  const rows = useRegardRows(worldId);
  const parties = useParties(worldId);
  const heroes = useWorldHeroes(worldId);
  const subject = (kind: number, id: bigint) => (kind === 0 ? `party ${parties.find((p) => p.id === id)?.name ?? `#${id}`}` : heroes.find((h) => h.id === id)?.name ?? `hero #${id}`);
  return (
    <Panel title="Ledger" label="Ledger" actions={named.length ? <Badge tone="plum">{named.length} named</Badge> : undefined}>
      <CardList data-testid="ledger">
        {named.length === 0 && <EmptyState glyph="◉" title="No one has taken your measure">Named characters who meet this World's parties keep a ledger of them — grudge, debt, fear, respect, familiarity.</EmptyState>}
        {named.map((n, k) => (
          <Card key={n.id.toString()} column index={k} className="fk-world__npc" data-testid="ledger-npc" data-npc={n.npc.toString()}>
            <div className="fk-card__row">
              <div className="fk-card__body">
                <div className="fk-card__title">{n.name} {n.live ? <Badge tone="green">live</Badge> : <Badge tone="grey">dead</Badge>}</div>
                <div className="fk-card__sub">{n.fights} fight{n.fights === 1 ? '' : 's'} · {n.survivals} survival{n.survivals === 1 ? '' : 's'}</div>
              </div>
            </div>
            {n.lastLine && <Quote className="fk-world__npc-line">“{n.lastLine}”</Quote>}
            <ul className="fk-world__regard">
              {rows.filter((r) => r.npc === n.npc).sort((a, b) => a.subjectKind - b.subjectKind || (a.subjectId < b.subjectId ? -1 : 1)).map((r) => {
                const known = r.knowledge.map((k) => actionById(k)?.name ?? `#${k}`);
                return (
                  <li key={r.id.toString()} className="fk-world__regard-row" data-testid="regard-row">
                    <span className="fk-world__regard-subject">{subject(r.subjectKind, r.subjectId)}{r.tension ? <Badge tone="amber">tension</Badge> : null}</span>
                    <span className="fk-world__regard-pips"><Pips value={r.grudge} label="grudge" /><Pips value={r.debt} label="debt" /><Pips value={r.fear} label="fear" /><Pips value={r.respect} label="respect" /><Pips value={r.familiarity} label="familiarity" /></span>
                    <span className="fk-muted fk-world__regard-knows">{known.length ? `knows: ${known.join(', ')}` : 'knows nothing of them yet'}</span>
                  </li>
                );
              })}
            </ul>
          </Card>
        ))}
      </CardList>
    </Panel>
  );
}

/** docs/119: the hero's Path page, with the skill points banked (and choices left open) on it. */
function PathButton({ h }: { h: Hero }) {
  const calling = useHeroCallings(h.worldId).find((c) => c.heroId === h.id);
  const row = useHeroProgress(h.worldId).find((r) => r.heroId === h.id);
  const w = useMemo(() => { try { return heroWalk(h.class, h.species, h.level, calling?.pick ?? 0, calling?.spells ?? [], row); } catch { return undefined; } }, [h.class, h.species, h.level, calling, row]);
  const open = w ? w.slots.filter((s) => s.level <= h.level && isOpen(s)).length : 0;
  const banked = w?.banked ?? 0;
  const label = [banked > 0 ? `${banked} skill point${banked === 1 ? '' : 's'} to spend` : '', open > 0 ? `${open} choice${open === 1 ? '' : 's'} left open` : ''].filter(Boolean).join(', ');
  return (
    <button type="button" className={`fk-world__chip-path ${banked > 0 || open > 0 ? 'fk-world__chip-path--ready' : ''}`} onClick={() => useOverlay.getState().openHeroPath(h.id)}
      title={`${h.name}'s path${label ? ` — ${label}` : ''}`} data-testid="hero-path-open" data-banked={banked}>
      ✦{banked > 0 ? <span className="fk-world__chip-path-n">{banked}</span> : null}
    </button>
  );
}

/** A party chip (my roster hero): portrait, name in my colour, where they stand, XP; opens the hero sheet. */
function HeroChip({ h, where, colour, open, onToggle, party }: { h: Hero; where: string; colour: string; open: boolean; onToggle: () => void; party: bigint | undefined }) {
  const levelUps = useMyLevelUpOffers();
  const offer = levelUps.find((o) => o.heroId === h.id);
  const incoming = useIncomingTrades(h.worldId).filter((t) => t.toHero === h.id).length;
  // docs/112 UP8: a wonder's gift of enchantment waiting on a piece to go on
  const gifts = useUpgradeGifts(h.worldId).filter((g) => g.heroId === h.id).length;
  const classId = CLASS_NAMES[h.class] ?? 'fighter';
  const next = h.level >= MAX_LEVEL ? undefined : xpToReach(h.level + 1);
  const floor = xpToReach(h.level);
  const species = cap(SPECIES_NAMES[h.species] ?? `species ${h.species}`);
  return (
    <div className={`fk-world__chip fk-hit ${open ? 'fk-world__chip--open' : ''} ${offer ? 'fk-world__chip--ready' : ''}`} style={{ ['--fk-seat' as string]: colour } as CSSProperties}
      data-testid="roster-hero" data-hero-name={h.name} data-status={HERO_STATUS[h.status]}>
      <button type="button" className="fk-world__chip-face" onClick={onToggle} aria-expanded={open} title={`${h.name} — L${h.level} ${cap(classId)} · ${species}`}>
        <span className="fk-world__chip-portrait"><HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={classId} scale={2} title={h.name} /></span>
        <span className="fk-world__chip-text">
          <span className="fk-world__chip-name">{h.name}<span className="fk-world__chip-level">L{h.level}</span></span>
          <span className="fk-world__chip-where">{h.status === 1 ? 'deployed · ' : ''}{where}</span>
          <span className="fk-visually-hidden" data-testid="hero-class-species">{cap(classId)} · {species}</span>
        </span>
      </button>
      <Meter kind="xp" size="sm" className="fk-world__chip-xp" data-testid="hero-xp" label="XP"
        value={next === undefined ? 1 : Math.max(0, h.xp - floor)} max={next === undefined ? 1 : Math.max(1, next - floor)}
        format={() => (next === undefined ? `${h.xp} xp · max level` : `${h.xp} / ${next} xp to L${h.level + 1}`)} />
      <button type="button" className="fk-world__chip-bag" onClick={() => openInventory(h.id)} title={`${h.name}'s inventory`} data-testid="inventory-toggle"><OryxIcon name="item_147" scale={1} /><span className="fk-visually-hidden">Inventory</span>{incoming > 0 && <span className="fk-world__chip-bag-dot" aria-label={`${incoming} trade offer${incoming === 1 ? '' : 's'}`} />}{gifts > 0 && <span className="fk-world__chip-bag-gift" title="A gift of enchantment waits" aria-label={`${gifts} gift${gifts === 1 ? '' : 's'} of enchantment`} data-testid="hero-gift-dot">✦</span>}</button>
      {offer && <button type="button" className="fk-world__chip-levelup" onClick={() => useOverlay.getState().openLevelUp(offer.id)} title={`${h.name} reaches level ${offer.toLevel}`} data-testid="level-up">Level up!</button>}
      {h.level > 1 && <PathButton h={h} />}
      {open && (
        <Panel variant="solid" className="fk-world__sheet" label={`${h.name}'s sheet`} data-testid="hero-sheet">
          <div className="fk-world__sheet-head">
            <Portrait heroId={h.id} species={h.species} frame={h.oryxFrame} classId={classId} scale={2} glow={!!offer} title={h.name} />
            <div className="fk-world__sheet-id">
              <div className="fk-world__sheet-name">{h.name} <ClassIcon classId={classId} title={cap(classId)} /> <Badge tone="gold">L{h.level}</Badge></div>
              <div className="fk-world__sheet-sub">{cap(classId)} · {species} · {h.build === 1 ? 'slight' : 'broad'}</div>
              <div className="fk-world__sheet-tags"><span>{HERO_STATUS[h.status]}</span><span>{h.fights} fight{h.fights === 1 ? '' : 's'}</span>{party !== undefined && <span>party #{party.toString()}</span>}<span className="fk-world__sheet-gold"><OryxIcon name="item_coin_bag" scale={1} /><Counter value={h.gold} /></span></div>
            </div>
          </div>
          <Meter kind="xp" size="sm" label="XP" value={next === undefined ? 1 : Math.max(0, h.xp - floor)} max={next === undefined ? 1 : Math.max(1, next - floor)} format={() => (next === undefined ? `${h.xp} xp · max level` : `${h.xp} / ${next} xp to L${h.level + 1}`)} />
          <GearStrip worldId={h.worldId} heroId={h.id} onOpen={() => openInventory(h.id)} />
        </Panel>
      )}
    </div>
  );
}

type DockTab = 'chat' | 'chronicle' | 'people' | 'ledger';

/** Bottom-left dock: chat (World / party), the Chronicle, who is here, the Ledger. Every tab stays mounted. */
export function Dock({ worldId, partyId }: { worldId: bigint; partyId: bigint | undefined }) {
  const me = useSession((s) => s.identity ?? '');
  const phase = useSession((s) => s.phase);
  const compat = useSession((s) => s.compat);
  const world = useWorld(worldId);
  const chronicle = useChronicle(worldId);
  const names = useNames(worldId);
  const settled = useSettled(1000, worldId);
  const [now, setNow] = useState(serverNow());
  useEffect(() => { const id = setInterval(() => setNow(serverNow()), 5000); return () => clearInterval(id); }, []);
  const online = useOnline(worldId, now);
  const [tab, setTab] = useState<DockTab>('chat');
  const tabs: Array<{ id: DockTab; label: ReactNode }> = [
    { id: 'chat', label: 'Chat' },
    { id: 'chronicle', label: <>Chronicle <span className="fk-world__dock-n">{chronicle.length}</span></> },
    { id: 'people', label: <>Here <span className="fk-world__dock-n">{online.length}</span></> },
    { id: 'ledger', label: 'Ledger' },
  ];
  return (
    <div className="fk-world__dock" data-testid="world-dock" data-tab={tab}>
      <div className="fk-world__dock-tabs" role="tablist" aria-label="Dock">
        {tabs.map((t) => <button key={t.id} type="button" role="tab" className="fk-world__dock-tab fk-hit" aria-selected={tab === t.id} onClick={() => setTab(t.id)} data-testid={`dock-${t.id}`}>{t.label}</button>)}
      </div>
      <Panel variant="solid" className="fk-world__dock-panel" label="Chat and the Chronicle">
      <div className="fk-world__dock-body" hidden={tab !== 'chat'}><ChatDock worldId={worldId} partyId={partyId} variant="panel" className="fk-world__chat" title="World chat" here={online.length} /></div>
      <div className="fk-world__dock-body fk-world__dock-scroll" hidden={tab !== 'chronicle'} data-sfx-scroll>
        {chronicle.length === 0 && (settled ? <EmptyState glyph="◉" title="Nothing written yet">Every fight, find and settlement in this World is written here.</EmptyState> : <SkeletonList n={3} kind="text" label="Loading the Chronicle" />)}
        <Feed data-testid="chronicle" className="fk-world__feed" items={chronicle.map((c) => ({ key: c.id.toString(), time: clock(tsToMs(c.at)), text: c.summary, glyph: '◉', glyphTitle: c.kindName }))} />
      </div>
      <div className="fk-world__dock-body fk-world__dock-scroll" hidden={tab !== 'people'}>
        <Divider tight>online</Divider>
        <div data-testid="online">
          {online.length === 0 && <p className="fk-faint fk-world__none">Nobody in the last minute.</p>}
          {online.length > 0 && <ul className="fk-world__people">{online.map((p) => <li key={p.id.toString()}><span className="fk-world__dot fk-world__dot--on" aria-hidden="true" /><span className="fk-world__person">{names[identityHex(p.owner)] ?? identityHex(p.owner).slice(-6)}</span><Badge tone="grey">{p.screen}</Badge></li>)}</ul>}
        </div>
        <Divider tight>members</Divider>
        <ul className="fk-world__people">{Object.entries(names).map(([id, n]) => <li key={id}><span className="fk-world__dot" aria-hidden="true" /><span className="fk-world__person">{n}</span>{id === me && <Badge tone="gold">you</Badge>}{identityHex(world?.owner) === id && <Badge tone="plum">keeper</Badge>}</li>)}</ul>
        <dl className="fk-world__session-list">
          <dt>Module</dt><dd className="fk-mono">{compat ? compat.moduleVersion : '—'}</dd>
          <dt>Ruleset</dt><dd className="fk-mono">{compat ? compat.rulesetVersion : 'no compat row'}</dd>
          <dt>Client</dt><dd className="fk-mono">{__FK_CLIENT_VERSION__}</dd>
          <dt>Phase</dt><dd>{phase}</dd>
        </dl>
      </div>
      <div className="fk-world__dock-body fk-world__dock-scroll" hidden={tab !== 'ledger'}><LedgerLite worldId={worldId} /></div>
      </Panel>
    </div>
  );
}

export function WorldScreen() {
  const worldId = useSession((s) => s.worldId);
  const go = useRoute((s) => s.go);
  const instanceId = useRoute((s) => s.instanceId);
  const routeParty = useRoute((s) => s.partyId);
  const world = useWorld(worldId);
  const account = useMyAccount();
  const member = account.find((m) => m.worldId === worldId);
  const heroes = useMyHeroes().filter((h) => h.worldId === worldId && h.status !== 2).sort((a, b) => (a.id < b.id ? -1 : 1));
  const allHeroes = useWorldHeroes(worldId);
  const partyMembers = useWorldPartyMembers(worldId);
  const instances = useWorldInstances(worldId);
  const locations = useLocationStatuses(worldId);
  const offersRaw = useOffers(worldId);
  const colours = usePlayerColours(worldId);
  const settings = useWorldSettings(worldId);
  const me = useSession((s) => s.identity ?? '');
  const [forging, setForging] = useState(false);
  const [editing, setEditing] = useState<Hero | undefined>();
  // docs/58: a player who joined a begun World outfits from their founding purse until they set out
  const [outfitting, setOutfitting] = useState(false);
  const { purse, recruit } = useFounding(worldId);
  const [openSheet, setOpenSheet] = useState<string | undefined>();
  const [picked, setPicked] = useState<string | undefined>();
  const [selNode, setSelNode] = useState<string | undefined>();
  const [saga, setSaga] = useState(false);
  const [entering, setEntering] = useState(false);
  const story = useWorldStory(worldId);
  const genesis = useGenesis(worldId);
  const opening = useWorldOpening(worldId);
  const seat = genesis ? display(genesis.haven.name) : worldTitle(world);
  const push = useToasts((s) => s.push);
  const names = useNames(worldId);
  const followHint = useRef(0);
  const clock = useWorldClock(worldId);
  const progress = useCampaignProgress(worldId);
  // docs/86: the storybook chapter being played (or being told)
  const book = useBookChapters(worldId);
  const bookNow = book[book.length - 1];
  // docs/24 K1: time moves only with finished fights and the storyteller's jumps; say so when it does
  const weekNow = clock?.weekIndex;
  const [seenWeek, setSeenWeek] = useState<bigint | undefined>();
  useEffect(() => {
    if (weekNow === undefined || !clock) return;
    if (seenWeek !== undefined && weekNow > seenWeek) {
      const n = Number(weekNow - seenWeek);
      push(`${n === 1 ? 'A week passes' : `${n} weeks pass`}: it is the ${weekPhrase(clock)}${clock.weather ? `, ${skyWords(clock.weather, clock.biome)} across the realm` : ''}.`, 'info');
    }
    setSeenWeek(weekNow);
  }, [weekNow]);

  const offers = useMemo(() => offersRaw.filter((o) => o.state === OFFER.OPEN).sort((a, b) => (a.slot < b.slot ? -1 : 1)), [offersRaw]);
  // docs/51: the villages the storyteller has written (a village still being written is not on the chart)
  const villagesRaw = useVillages(worldId);
  const villages = useMemo(() => villagesRaw.filter((v) => v.state === 1).sort((a, b) => a.index - b.index), [villagesRaw]);
  useEffect(() => { if (worldId !== undefined && member && genesis) void rpc.ensureVillages(worldId); }, [worldId, !!member, !!genesis]);
  // docs/73 TP1: my band's lead has walked the band into a village: I go in with it
  const myPresence = useVillagePresence(worldId).find((p) => identityHex(p.owner) === me);
  useEffect(() => {
    if (!myPresence || myPresence.villageId === 0n || identityHex(myPresence.movedBy) === me) return;
    const v = villages.find((x) => x.id === myPresence.villageId);
    if (!v) return;
    push(`Your band walks into ${v.name}.`, 'info');
    go('village', { villageId: v.id });
  }, [myPresence?.villageId, tsToMs(myPresence?.at), villages.length]);
  // Places: the World itself (the haven, a village too), then every location (rows by index, plus
  // any an offer names first), then the other villages; a village's notices stand on its node.
  const places = useMemo<Place[]>(() => {
    const byIndex = new Map<number, Place>();
    for (const l of [...locations].sort((a, b) => a.locationIndex - b.locationIndex)) if (l.locationIndex < VILLAGE_BASE) byIndex.set(l.locationIndex, { key: locKey(l.locationIndex), name: l.name, loc: l, offers: [] });
    const home: Place = { key: 'home', name: seat ?? 'The World', home: true, village: villages.find((v) => v.index === 0), offers: [] };
    for (const v of villages) if (v.index > 0) byIndex.set(v.locationIndex, { key: locKey(v.locationIndex), name: v.name, village: v, offers: [] });
    for (const o of offers) {
      if (o.locationIndex === VILLAGE_BASE) { home.offers.push(o); continue; }
      const p = byIndex.get(o.locationIndex) ?? { key: locKey(o.locationIndex), name: o.location, offers: [] };
      p.offers.push(o);
      byIndex.set(o.locationIndex, p);
    }
    return [home, ...[...byIndex.entries()].sort((a, b) => a[0] - b[0]).map(([, p]) => p)];
  }, [locations, offers, seat, villages]);

  // docs/36: the chart is generated here (the walk, the quest card and the map share it), each
  // place read through its name, its slot and the story's line about it
  const walks = usePartyWalks(worldId);
  const drawnWalks = useRef(new Map<string, BandWalk>());   // each band's walk as last drawn (for a redirect's lead-in)
  const parties = useParties(worldId);
  const seed = seedFrom(world?.worldSeed, world?.id);
  const siteKey = places.map((p) => `${p.key}:${p.name}`).join('|');
  const sites = useMemo<MapSite[]>(() => places.map((p) => {
    const slot = p.home ? 'home' : slotOfKey(p.key);
    const text = p.village && !p.home ? p.village.text : !genesis ? '' : slot === 'home' ? genesis.haven.text : slot ? genesis.places[slot].text : '';
    // docs/51: a village stands as a town whatever its name says
    return { key: p.key, name: p.name, text, ...(slot ? { slot } : {}), ...(p.village && !p.home ? { kind: 'town' as const } : {}) };
  }), [siteKey, genesis]);
  // docs/143 OW3: the valley is shaped for the realm's country
  const chartSky = useChartSky(worldId);
  const owBiome = artBiome(chartSky.biome);
  const ow = useMemo(() => generateOverworld(seed, sites, { biome: owBiome }), [seed, sites, owBiome]);
  // docs/44: the haven's streets while it is the place in hand (by day, or a week that finds it at night);
  useEffect(() => { setPlaceSeed(worldId?.toString() ?? ''); }, [worldId]);
  const atHaven = !!selNode && !!places.find((p) => p.key === selNode)?.home;
  // a hero's sheet is where heroes sell to the shop: the potion shop's murmur while it is open
  usePlace(openSheet ? { key: 'place.shop', gain: 0.8 } : atHaven ? { key: 'place.town', band: townBand(weekNow), gain: 0.8 } : undefined);
  // docs/36: an in-world lobby — every player here stands in a band, mustered from their free heroes
  // (a story still being written has no opening row yet: its first battle is still to come)
  const openingDone = story?.state === STORY.TOLD && (!opening || opening.phase === OPENING.DONE);
  const loose = heroes.filter((h) => h.status === 0 && !partyMembers.some((m) => m.heroId === h.id)).length;
  useEffect(() => { if (worldId !== undefined && member && openingDone && loose > 0) void rpc.muster(worldId); }, [worldId, !!member, openingDone, loose]);

  if (!worldId || !member) return <><Shell /><PlayOnline /></>;

  const placeOf = (o: Offer) => keyOfLocation(o.locationIndex);
  const placeName = (o: Offer) => places.find((p) => p.key === placeOf(o))?.name ?? o.location;
  const nodeOffers = selNode ? places.find((p) => p.key === selNode)?.offers ?? [] : [];
  // The notice in hand: the one picked, else the selected node's first, else the first main offer, else the first.
  const featured = offers.find((o) => o.id.toString() === picked) ?? nodeOffers[0] ?? offers.find((o) => !o.side) ?? offers[0];
  const pick = (o: Offer) => { setPicked(o.id.toString()); setSelNode(placeOf(o)); };
  const selectNode = (key: string) => {
    setSelNode(key); const first = places.find((p) => p.key === key)?.offers[0]; setPicked(first?.id.toString()); walkTo(key);
    // docs/78 BW1: a follower's click only looks; say once in a while who walks the band
    if (myBand?.partyId && !myBand.lead && bandPlace(ow, myBand, serverNow()) !== key && serverNow() - followHint.current > 20_000) {
      followHint.current = serverNow();
      push(`${leadName(myBand)} leads your band. Right-click the chart to ping a place for them.`, 'info');
    }
  };
  const leadName = (b: BandView) => (b.leadOwner ? names[b.leadOwner] ?? b.leadOwner.slice(-6) : 'The lead');

  const partyOf = new Map(partyMembers.map((m) => [m.heroId.toString(), m.partyId]));
  const live = instances.filter((i) => i.state === INST.LOADED || i.state === INST.IN_ENCOUNTER);
  const placeByName = new Map(places.map((p) => [norm(p.name), p.key]));

  // docs/36: the bands on the chart — every party with heroes, where its walk row says (else where
  // its adventure is, else the seat); heroes in no band stand at the seat
  const heroById = new Map(allHeroes.map((h) => [h.id.toString(), h]));
  const walkOf = new Map(walks.map((w) => [w.partyId.toString(), w]));
  const inBand = new Set<string>();
  const bands: BandView[] = [];
  for (const p of [...parties].sort((a, b) => (a.id < b.id ? -1 : 1))) {
    if (p.state === 2) continue;
    const ms = partyMembers.filter((m) => m.partyId === p.id);
    const hs = ms.map((m) => heroById.get(m.heroId.toString())).filter((h): h is Hero => !!h && h.status !== 2).sort((a, b) => (a.id < b.id ? -1 : 1));
    if (!hs.length) continue;
    for (const h of hs) inBand.add(h.id.toString());
    const w = walkOf.get(p.id.toString());
    const inst = live.find((i) => i.partyId === p.id);
    let walk: BandWalk | undefined;
    if (w) {
      walk = { from: placeKeyOf(w.fromPlace), to: placeKeyOf(w.toPlace), departed: tsToMs(w.departedAt) ?? 0 };
      // a walk redirected on the way sets out from where the old one stood when it turned
      const prev = drawnWalks.current.get(p.id.toString());
      if (prev && prev.from === walk.from && prev.to === walk.to && prev.departed === walk.departed) walk = prev;
      else if (prev) {
        const turns = walkTurns(ow, prev, walk.departed);
        const lead = !turns ? undefined : walk.from === prev.from ? turns.from : walk.from === prev.to ? turns.to : undefined;
        if (lead && pathLength(lead) > 0.01) walk.lead = lead;
        // docs/78 BW2: the followers keep walking the road they were on into the turn
        const trail = turns && bandTrail(ow, prev);
        if (trail) {
          const d = trail.tailLen + Math.max(0, ((walk.departed - prev.departed) / 1000) * WALK_CELLS_PER_S);
          const tail = subPath(trail.route, Math.max(0, d - BAND_GAP * BAND_SHOWN), d);
          if (pathLength(tail) > 0.01) walk.tail = tail;
        }
      }
      drawnWalks.current.set(p.id.toString(), walk);
    }
    bands.push({
      id: `party-${p.id}`, partyId: p.id, name: p.name, heroes: hs,
      at: w ? placeKeyOf(w.toPlace) : (inst && placeByName.get(norm(inst.location))) ?? 'home',
      walk,
      mine: ms.some((m) => identityHex(m.owner) === me), lead: identityHex(p.lead) === me, out: p.state !== 0, leadOwner: identityHex(p.lead),
    });
  }
  const strays = allHeroes.filter((h) => h.status !== 2 && !inBand.has(h.id.toString())).sort((a, b) => (a.id < b.id ? -1 : 1));
  if (strays.length) bands.push({ id: 'seat', name: 'At the seat', heroes: strays, at: 'home', mine: false, lead: false, out: false });
  const myBand = bands.find((b) => b.mine && b.lead && !b.out) ?? bands.find((b) => b.mine && !b.out);
  const heroPlace = (h: Hero): string => { const b = bands.find((x) => x.heroes.some((y) => y.id === h.id)); return b ? bandPlace(ow, b, serverNow()) : 'home'; };
  // Clicking a place walks my band there along the roads (docs/78 BW1: only its lead walks it).
  const walkTo = (key: string): number => {
    const b = myBand;
    if (!b?.partyId || !b.lead || !ow.nodes[key]) return 0;
    const now = serverNow();
    if (b.walk?.to === key) { const plan = walkPlan(ow, b.walk.from, key, b.walk.lead); return plan ? Math.max(0, b.walk.departed + plan.ms - now) : 0; }
    // mid-walk, turn round at whichever end of the road makes the shorter way there
    const turns = b.walk && walkTurns(ow, b.walk, now);
    const ways = turns
      ? ([[b.walk!.from, turns.from], [b.walk!.to, turns.to]] as const).map(([from, lead]) => ({ from, plan: walkPlan(ow, from, key, lead) }))
      : [{ from: bandPlace(ow, b, now), plan: undefined }].map((x) => ({ ...x, plan: x.from === key ? undefined : walkPlan(ow, x.from, key) }));
    const way = ways.filter((x) => x.plan).sort((x, y) => x.plan!.ms - y.plan!.ms)[0];
    if (!way) return 0;   // (the click that walks the band sounds from its own button)
    void rpc.walkBand(b.partyId, placeNumOf(way.from), placeNumOf(key));
    return way.plan!.ms;
  };
  const nameOfPlace = (k: string) => titled(places.find((p) => p.key === k)?.name ?? '');
  // docs/51: the village in hand, and walking into it (the band walks there first)
  const selVillage = places.find((p) => p.key === (selNode ?? ''))?.village;
  const villageHere = !!selVillage && (!myBand || bandPlace(ow, myBand, serverNow()) === selNode);
  const enterVillage = async (v: VillageRow) => {
    const key = v.index === 0 ? 'home' : locKey(v.locationIndex);
    setEntering(true);
    const wait = walkTo(key);
    if (wait > 0) await new Promise((r) => setTimeout(r, Math.min(wait, 6000) + 150));
    const r = await rpc.enterVillage(v.id);
    setEntering(false);
    if (r.ok) go('village', { villageId: v.id });
  };

  const rosterFull = !recruit.ok;
  const gold = heroes.reduce((s, h) => s + h.gold, 0);
  const myColour = playerColour(colours.get(me));
  const canChooseColour = colourOpen({ begun: settings?.phase !== 0, begunAtMs: tsToMs(settings?.begunAt), joinedAtMs: tsToMs(member?.joinedAt), inBand: heroes.some((h) => partyOf.has(h.id.toString())) });
  return (
    <main className="fk-world" data-testid="world-screen">
      <Overworld worldId={worldId} ow={ow} places={places} selected={selNode ?? (featured ? placeOf(featured) : undefined)} onSelect={selectNode} bands={bands} colours={colours} featured={featured} sky={chartSky} />
      <BandPanel worldId={worldId} bands={bands} mine={myBand} openingDone={openingDone} hasHeroes={heroes.length > 0} colours={colours} canChooseColour={canChooseColour} />

      <header className="fk-world__bar">
        <div className="fk-world__lead">
          <div className="fk-world__plate fk-hit"><ShellPlate /></div>
          <div className="fk-world__nav fk-hit"><ShellNav /><ShellName label={false} /></div>
        </div>
        <div className="fk-world__chips" data-testid="roster" aria-label="Roster">
          {heroes.map((h) => (
            <HeroChip key={h.id.toString()} h={h} where={nameOfPlace(heroPlace(h))} colour={myColour} party={partyOf.get(h.id.toString())}
              open={openSheet === h.id.toString()} onToggle={() => setOpenSheet(openSheet === h.id.toString() ? undefined : h.id.toString())} />
          ))}
          {(purse.open || heroes.length === 0) && heroes.length < MAX_HEROES && (
            <button type="button" className="fk-world__recruit fk-hit" disabled={rosterFull} onClick={() => setForging(true)} data-testid="hero-create" data-fee={recruit.fee}
              title={recruit.why ?? (recruit.fee ? `Recruit a hero for ${recruit.fee} founding gold (${heroes.length} of ${MAX_HEROES})` : 'Forge your first hero')}>
              <span className="fk-world__recruit-plus" aria-hidden="true">+</span>
              <span className="fk-world__recruit-text">{heroes.length === 0 ? 'Recruit your first hero' : 'Recruit'}<small>{recruit.fee ? `${recruit.fee}g · ` : ''}{heroes.length} / {MAX_HEROES}</small></span>
            </button>
          )}
          {purse.open && (
            <button type="button" className="fk-world__recruit fk-world__outfit fk-hit" onClick={() => setOutfitting(true)} data-testid="outfit-open" title="Spend your founding gold before you set out">
              <span className="fk-world__recruit-plus" aria-hidden="true"><OryxIcon name="item_coin_bag" scale={1} /></span>
              <span className="fk-world__recruit-text">Outfit<small>{purse.left} founding gold</small></span>
            </button>
          )}
        </div>
        <div className="fk-world__purse fk-hit">
          <span className="fk-world__gold" title={`${gold} gold across your heroes`}><OryxIcon name="item_coin_bag" scale={2} /><Counter value={gold} /></span>
          {instanceId && <Button small onClick={() => go('localmap')} data-testid="to-localmap">Local map</Button>}
          {genesis && <Button small onClick={() => setSaga(true)} data-sfx="cine.saga" data-testid="to-saga" title={`The Saga of ${display(genesis.realm.name)}`}>Saga</Button>}
          <Button small primary onClick={() => go('lobby')} data-testid="to-lobby">To the lobby</Button>
          <ShellMenuButton />
        </div>
      </header>

      <Dock worldId={worldId} partyId={routeParty} />
      <ul className="fk-world__legend fk-hit" aria-label="Map legend">
        <li><i className="fk-world__key fk-world__key--main" />Main quest</li>
        <li><i className="fk-world__key fk-world__key--side" />Side quest</li>
        <li><i className="fk-world__key fk-world__key--waypoint" />Cleared · waypoint</li>
        <li><i className="fk-world__key fk-world__key--village" />Village</li>
        <li><i className="fk-world__key fk-world__key--locked" />Locked</li>
      </ul>
      {opening?.phase === OPENING.WAITING && genesis && (
        <div className="fk-world__opening fk-hit" data-testid="opening-banner">
          <span className="fk-world__opening-text"><span>The first battle awaits</span><b>{genesis.battle.title}</b></span>
          <Button small primary onClick={() => go('intro', { replay: true })} data-testid="opening-hear">Hear the call</Button>
        </div>
      )}
      {progress?.writing && (
        <div className="fk-world__opening fk-hit" data-testid="quest-writing-banner">
          <span className="fk-world__opening-text"><span>The storyteller is writing</span><b data-testid="quest-writing-what">{bookNow?.state === CHAPTER.CLOSING ? `Chapter ${numberWord(bookNow.number + 1)} of the tale` : 'the next main quest'}</b></span>
        </div>
      )}
      {story?.state === STORY.FAILED && (
        <div className="fk-world__opening fk-hit" data-testid="story-failed-banner">
          <span className="fk-world__opening-text"><span>The storyteller could not write</span><b>{isNamed(world) ? worldTitle(world) : 'this World'}</b></span>
          <Button small primary onClick={() => void rpc.retryStory(worldId)} data-testid="story-retry-banner">Ask again</Button>
        </div>
      )}
      {story && story.state === STORY.WRITING && (
        <div className="fk-world__opening fk-hit" data-testid="story-writing-banner">
          <span className="fk-world__opening-text"><span>The storyteller is writing</span><b>{isNamed(world) ? worldTitle(world) : 'this World'}</b></span>
        </div>
      )}
      {selVillage && <VillageCard v={selVillage} here={villageHere} busy={entering} onEnter={() => void enterVillage(selVillage)} follow={myBand && !myBand.lead && !villageHere ? leadName(myBand) : undefined} />}
      <SagaModal open={saga} onClose={() => setSaga(false)} g={genesis} source={story?.source ?? 0} onReplay={() => { setSaga(false); go('intro', { replay: true }); }}
        onReplayChapter={(n) => { setSaga(false); go('chapter', { replay: true, chapter: n }); }} />
      <QuestBoard worldId={worldId} offers={offers} featured={featured} onPick={pick} placeName={placeName} setOut={(o) => walkTo(placeOf(o))} almanac={
        <Almanac clock={clock} className="fk-world__almanac fk-hit" />
      } />

      <Modal open={forging} onClose={() => setForging(false)} eyebrow={worldTitle(world)} title="Recruit a hero" size="full" testId="forge-modal">
        <HeroForge worldId={worldId} rosterFull={rosterFull} fee={recruit.fee} capNote={recruit.why} onForged={() => setForging(false)} />
      </Modal>
      <Modal open={outfitting && purse.open} onClose={() => setOutfitting(false)} eyebrow={worldTitle(world)} title="Outfit your company" size="wide" testId="outfit-modal">
        <OutfitPanel worldId={worldId} arrival onRecruit={() => { setOutfitting(false); setForging(true); }} onEdit={(h) => { setOutfitting(false); setEditing(h); }} />
      </Modal>
      <Modal open={!!editing} onClose={() => { setEditing(undefined); setOutfitting(true); }} eyebrow={worldTitle(world)} title={`Edit ${editing?.name ?? 'hero'}`} size="full" testId="forge-edit-modal">
        {editing && <HeroForge key={editing.id.toString()} worldId={worldId} hero={editing} onForged={() => { setEditing(undefined); setOutfitting(true); }} />}
      </Modal>
    </main>
  );
}

/**
 * docs/36: the in-world lobby, top left over the chart. My band (mustered from my free heroes the
 * moment I am here), who leads it, the other bands in the World to join, and a way back out on my
 * own. No lobby screen in between: the quest card's Accept sets the band out.
 * docs/184: the swatch before the band's name chooses my player colour, only for a late joiner until
 * their band forms (PC10); everyone else's colour was set in the lobby.
 */
function BandPanel({ worldId, bands, mine, openingDone, hasHeroes, colours, canChooseColour }: { worldId: bigint; bands: BandView[]; mine: BandView | undefined; openingDone: boolean; hasHeroes: boolean; colours: Map<string, number>; canChooseColour: boolean }) {
  const names = useNames(worldId);
  const parties = useParties(worldId);
  const [busy, setBusy] = useState<string | undefined>();
  const run = async (key: string, fn: () => Promise<unknown>) => { setBusy(key); await fn(); setBusy(undefined); };
  const others = bands.filter((b) => b.partyId !== undefined && b !== mine && !b.out);
  const leadOf = (b: BandView) => { const p = parties.find((x) => x.id === b.partyId); return p ? names[identityHex(p.lead)] ?? identityHex(p.lead).slice(-6) : '?'; };
  return (
    <Panel variant="solid" className="fk-world__bandbox fk-hit" label="Your band" data-testid="band-panel" data-band={mine?.id ?? ''}>
      <header className="fk-world__section-head">{canChooseColour && <ColourButton worldId={worldId} />}<span className="fk-world__section-title">{mine ? mine.name : 'Your band'}</span>{mine && <Badge tone={mine.lead ? 'gold' : 'grey'}>{mine.lead ? 'you lead' : `led by ${leadOf(mine)}`}</Badge>}</header>
      {mine ? (
        <ul className="fk-world__band-heroes" data-testid="band-heroes">
          {mine.heroes.map((h) => <li key={h.id.toString()} style={{ ['--fk-seat' as string]: playerColour(colours.get(identityHex(h.owner))) } as CSSProperties} data-testid="band-hero" data-hero-name={h.name}>{h.name}</li>)}
        </ul>
      ) : (
        <p className="fk-faint fk-world__band-none" data-testid="band-none">{!hasHeroes ? 'Recruit a hero and your band musters here.' : !openingDone ? 'Your band musters once the first battle is fought.' : 'Mustering your band…'}</p>
      )}
      {mine && <p className="fk-faint fk-world__band-hint" data-testid="band-hint">{mine.lead ? 'Click a place to walk the band there; Accept on a quest sets out.' : `${leadOf(mine)} walks the band.`} Right-click the chart to ping a place.</p>}
      {(others.length > 0 || (mine && !mine.lead)) && (
        <ul className="fk-world__band-others">
          {others.map((b) => (
            <li key={b.id} data-testid="band-other" data-band-name={b.name}>
              <span>{b.name} <small className="fk-faint">· {b.heroes.length} hero{b.heroes.length === 1 ? '' : 'es'}</small></span>
              <Button small disabled={!!busy} loading={busy === b.id} onClick={() => void run(b.id, () => rpc.joinBand(b.partyId!))} data-testid="band-join">Join</Button>
            </li>
          ))}
          {mine && !mine.lead && <li><span className="fk-faint">Go your own way</span><Button small variant="ghost" disabled={!!busy} loading={busy === 'solo'} onClick={() => void run('solo', () => rpc.goSolo(worldId))} data-testid="band-solo">Go solo</Button></li>}
        </ul>
      )}
    </Panel>
  );
}
